'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CredentialField, ConnectionConfigField } from '@/lib/integrations/types';
import { Loader2, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import type { AdvancedOverrides } from '@/lib/integrations/types';

interface CredentialsFormProps {
  fields: CredentialField[];
  connectionConfigFields?: ConnectionConfigField[];
  onSubmit: (
    credentials: Record<string, string>,
    displayName?: string,
    advancedOverrides?: AdvancedOverrides,
    connectionConfig?: Record<string, string>
  ) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  providerName?: string;
}

// Build zod schema dynamically based on credential and connection config fields
function buildSchema(fields: CredentialField[], connectionConfigFields?: ConnectionConfigField[]) {
  const shape: Record<string, z.ZodString> = {
    displayName: z.string().optional() as unknown as z.ZodString,
    baseUrl: z.string().optional() as unknown as z.ZodString,
  };

  for (const field of fields) {
    if (field.required) {
      shape[field.name] = z.string().min(1, `${field.label} is required`);
    } else {
      shape[field.name] = z.string().optional() as unknown as z.ZodString;
    }
  }

  // Add connection config fields with cc_ prefix to avoid name collisions
  if (connectionConfigFields) {
    for (const field of connectionConfigFields) {
      const key = `cc_${field.name}`;
      if (field.required) {
        shape[key] = z.string().min(1, `${field.label} is required`);
      } else {
        shape[key] = z.string().optional() as unknown as z.ZodString;
      }
    }
  }

  return z.object(shape);
}

export function CredentialsForm({
  fields,
  connectionConfigFields,
  onSubmit,
  onCancel,
  loading = false,
  providerName,
}: CredentialsFormProps) {
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const schema = buildSchema(fields, connectionConfigFields);
  type FormData = z.infer<typeof schema>;

  const hasConnectionConfig = connectionConfigFields && connectionConfigFields.length > 0;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const togglePassword = (fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const onFormSubmit = async (data: FormData) => {
    const { displayName, baseUrl, ...rest } = data;

    // Separate credentials from connection config (cc_ prefixed)
    const credentials: Record<string, string> = {};
    const connectionConfig: Record<string, string> = {};

    for (const [key, value] of Object.entries(rest)) {
      if (key.startsWith('cc_')) {
        connectionConfig[key.slice(3)] = value as string;
      } else {
        credentials[key] = value as string;
      }
    }

    const advancedOverrides = baseUrl ? { base_url: baseUrl as string } : undefined;
    await onSubmit(
      credentials,
      displayName as string | undefined,
      advancedOverrides,
      hasConnectionConfig ? connectionConfig : undefined
    );
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      {/* Connection config fields (subdomain, etc.) */}
      {hasConnectionConfig && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Please provide the following configuration details:
          </p>
          {connectionConfigFields!.map((field) => {
            const fieldKey = `cc_${field.name}`;
            return (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={fieldKey}>
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </Label>
                <div className="flex items-center">
                  {field.prefix && (
                    <span className="text-sm text-muted-foreground bg-muted px-2 py-2 rounded-l-md border border-r-0">
                      {field.prefix}
                    </span>
                  )}
                  <Input
                    id={fieldKey}
                    placeholder={field.placeholder}
                    className={field.prefix || field.suffix ? 'rounded-none flex-1' : ''}
                    {...register(fieldKey)}
                  />
                  {field.suffix && (
                    <span className="text-sm text-muted-foreground bg-muted px-2 py-2 rounded-r-md border border-l-0">
                      {field.suffix}
                    </span>
                  )}
                </div>
                {field.description && (
                  <p className="text-xs text-muted-foreground">{field.description}</p>
                )}
                {errors[fieldKey] && (
                  <p className="text-xs text-destructive">
                    {errors[fieldKey]?.message as string}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="displayName">Connection Name (optional)</Label>
        <Input
          id="displayName"
          placeholder={providerName ? `My ${providerName} Connection` : 'My Connection'}
          {...register('displayName')}
        />
        <p className="text-xs text-muted-foreground">
          A friendly name to identify this connection
        </p>
      </div>

      {fields.map((field) => (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name}>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <div className="relative">
            {field.type === 'textarea' ? (
              <textarea
                id={field.name}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={field.placeholder}
                {...register(field.name)}
              />
            ) : (
              <>
                <Input
                  id={field.name}
                  type={
                    field.type === 'password' && !showPasswords[field.name]
                      ? 'password'
                      : 'text'
                  }
                  placeholder={field.placeholder}
                  {...register(field.name)}
                />
                {field.type === 'password' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => togglePassword(field.name)}
                  >
                    {showPasswords[field.name] ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </Button>
                )}
              </>
            )}
          </div>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          {errors[field.name] && (
            <p className="text-xs text-destructive">
              {errors[field.name]?.message as string}
            </p>
          )}
        </div>
      ))}

      {/* Advanced Options */}
      <div className="border rounded-md">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-left hover:bg-muted/50"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
          Advanced Options
        </button>
        {showAdvanced && (
          <div className="px-3 pb-3 space-y-2">
            <Label htmlFor="baseUrl">Custom Base URL (optional)</Label>
            <Input
              id="baseUrl"
              placeholder="https://api.example.com"
              {...register('baseUrl')}
            />
            <p className="text-xs text-muted-foreground">
              For self-hosted or regional deployments. Leave empty for default.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
          Connect
        </Button>
      </div>
    </form>
  );
}
